import React from "react";
import ClaimButton from "@components/FlowActionButtons/ClaimButton";
import { useNotification } from "@context/NotificationContext";
import { canUserClaim } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/flow/utils";
import type { ShippingAgencyTransfer } from "@services/shipping-agency-transfer/types";
import { claimShippingAgencyTransferById } from "@services/shippingAgencyTransferApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

interface ClaimShippingAgencyTransferProps {
  transferDetails: ShippingAgencyTransfer | null;
}

const LANG_BASE = "vessel-call.shipping-agency-transfer";
const SERVICE = "bff-vessel-call";

const ClaimShippingAgencyTransfer: React.FC<ClaimShippingAgencyTransferProps> = ({
  transferDetails,
}) => {
  const { notify } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const claimMutation = useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "ShippingAgencyTransfer",
          action: "claim_shipping_agency_transfer",
          entityType: "shipping_agency_transfer",
        },
        (transactionId) => claimShippingAgencyTransferById({ service: SERVICE, id, transactionId }),
      ),
    onSuccess: async () => {
      const transferId = transferDetails?.id;
      const vesselCallId = transferDetails?.vesselCallId;

      if (transferId) {
        await queryClient.invalidateQueries({ queryKey: ["shipping-agency-transfer", transferId] });
      }

      if (vesselCallId) {
        await queryClient.invalidateQueries({
          queryKey: ["getShippingAgencyTransfers", vesselCallId],
        });
      }
      notify({
        title: t(`${LANG_BASE}.notifications.transfer-claimed`),
        type: "success",
      });
    },
    onError: (error: Error) =>
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      }),
  });

  if (!transferDetails?.task) {
    return null;
  }

  const isActionAvailable = canUserClaim(
    transferDetails.task.status,
    transferDetails.status,
    transferDetails.newShippingAgency.companyCode,
  );

  const handleClaim = () => {
    claimMutation.mutate(transferDetails.id);
  };

  return (
    <ClaimButton
      langBase={`${LANG_BASE}.flow-buttons`}
      permission="canClaimShippingAgencyTransfer"
      isActionAvailable={isActionAvailable}
      onClaim={handleClaim}
    />
  );
};

export default ClaimShippingAgencyTransfer;
